import type {
  IntelligencePayload, OverviewPayload, DecisionContext, StayStage,
  OverviewSnapshot, ReviewTeaserPayload, SinceLastVisitItem,
} from "./types"
import type { PerformanceSummaryResponse, SeasonContext } from "@/lib/api"

export const STAY_LABELS: Record<string, string> = {
  short: "Short stay",
  standard: "Standard stay",
  long: "Long stay",
}

export const PARTY_LABELS: Record<string, string> = {
  solo: "Solo",
  couple: "Couple",
  family: "Family",
  group: "Group",
  unknown: "Guest count unknown",
}

export const PHASE_ICONS: Record<string, string> = {
  before_checkin: "\u2709",
  arrival_day: "\u2600",
  during_stay: "\u2302",
  checkout_evening: "\u263D",
  checkout_morning: "\u2714",
}

export const SEGMENT_OPACITIES = [0.6, 0.4, 0.25, 0.12]

/**
 * Format a backend percentage value for display.
 * Backend sends values already as percentages (e.g. -24.6 means -24.6%).
 * Named explicitly so future devs don't "fix" it by multiplying by 100.
 */
export function formatPct(value: number | null | undefined): string {
  if (value == null) return "—"
  return `${value.toFixed(1)}%`
}

/**
 * Format a backend percentage as a rounded integer.
 * Backend sends values already as percentages (e.g. 55.0 means 55%).
 */
export function formatPctRound(value: number | null | undefined): string {
  if (value == null) return "—"
  return `${Math.round(value)}%`
}

export function formatDecimal(value: unknown, digits = 1): string {
  const numeric = typeof value === "number" ? value : typeof value === "string" ? Number(value) : NaN
  if (!Number.isFinite(numeric)) return "—"
  return numeric.toFixed(digits)
}

export function toDateOnly(value: string | null | undefined): string {
  const normalized = (value || "").trim()
  if (!normalized) return ""
  const match = normalized.match(/^(\d{4}-\d{2}-\d{2})/)
  return match?.[1] || normalized
}

export function parseDateOnlyAtMidday(value: string | null | undefined): Date | null {
  const dateOnly = toDateOnly(value)
  if (!dateOnly) return null
  const date = new Date(`${dateOnly}T12:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return date
}

export function createSafeCurrencyFormatter(currency: string | null | undefined, locale?: string): Intl.NumberFormat {
  const normalized = (currency || "").trim().toUpperCase()
  const safeCurrency = /^[A-Z]{3}$/.test(normalized) ? normalized : "USD"
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: safeCurrency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  } catch {
    return new Intl.NumberFormat(locale, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    })
  }
}

export function formatPartyLabel(
  adults?: number | null,
  children?: number | null,
  infants?: number | null,
): string {
  const parts: string[] = []
  if (adults != null && adults > 0) {
    parts.push(`${adults} adult${adults !== 1 ? "s" : ""}`)
  }
  if (children != null && children > 0) {
    parts.push(`${children} child${children !== 1 ? "ren" : ""}`)
  }
  if (infants != null && infants > 0) {
    parts.push(`${infants} infant${infants !== 1 ? "s" : ""}`)
  }
  return parts.join(", ") || ""
}

export function resolveCurrentPhase(arrivalDate: string, departureDate: string): string | null {
  const today = new Date().toISOString().slice(0, 10)
  const arrival = toDateOnly(arrivalDate)
  const departure = toDateOnly(departureDate)
  if (!arrival || !departure) return null
  if (today < arrival) return "before_checkin"
  if (today === arrival) return "arrival_day"
  if (today === departure) return "checkout_morning"
  const dep = parseDateOnlyAtMidday(departure)
  if (!dep) return null
  dep.setDate(dep.getDate() - 1)
  const eveningDate = dep.toISOString().slice(0, 10)
  if (today === eveningDate) return "checkout_evening"
  if (today > arrival && today < departure) return "during_stay"
  return null
}

export function normalizeStatus(value: string): "healthy" | "warning" | "error" | "neutral" {
  const next = (value || "").toLowerCase()
  if (next === "healthy" || next === "warning" || next === "error") return next
  return "neutral"
}

export function statusLabel(value: string): string {
  const next = (value || "").toLowerCase()
  if (next === "healthy") return "Healthy"
  if (next === "warning") return "Needs attention"
  if (next === "error") return "Error"
  return "Not configured"
}

export function normalizeGuidebookAccessStatus(value: string): "view" | "success" | "failed" | "unknown" {
  const next = (value || "").toLowerCase().trim()
  if (next === "view") return "view"
  if (next === "success") return "success"
  if (next === "failed") return "failed"
  return "unknown"
}

export function guidebookAccessStatusLabel(value: string): string {
  const next = normalizeGuidebookAccessStatus(value)
  if (next === "view") return "Viewed"
  if (next === "success") return "Auth success"
  if (next === "failed") return "Auth failed"
  return "Unknown"
}

export function guidebookAccessSourceLabel(source: string): string {
  const next = (source || "").trim().toLowerCase()
  if (!next) return "Unknown source"
  if (next === "pms_lodgify") return "PMS (Lodgify)"
  if (next === "ical_dates") return "iCal date match"
  if (next === "ical") return "iCal name match"
  return next.replace(/_/g, " ")
}

export function formatDateLabel(isoDate: string): string {
  const date = parseDateOnlyAtMidday(isoDate)
  if (!date) return isoDate || "\u2014"
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  })
}

export function formatDateTime(isoString: string): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return "Unknown time"
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function formatRelativeTime(isoString: string): string {
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return "Unknown"
  const now = Date.now()
  const diffMs = now - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return "Now"
  if (diffMin < 60) return `${diffMin} min ago`
  if (diffHr < 24) return `${diffHr} hrs ago`
  if (diffDay < 7) return `${diffDay} ${diffDay === 1 ? "day" : "days"} ago`
  if (diffDay < 30) return `${Math.floor(diffDay / 7)} wks ago`
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

/**
 * Build a deterministic identity line from intelligence data.
 * Template: "A {season}-led home for {topSegment}, with {arrivalPattern} arrivals and a mostly {audience} audience."
 */
export function buildIdentityLine(intelligence: IntelligencePayload | null | undefined): string | null {
  if (!intelligence?.available) return null

  const parts: string[] = []

  // Season — parse from heroStats narrative (look for season words)
  const narrative = intelligence.heroStats?.narrative?.toLowerCase() || ""
  const seasonMatch = narrative.match(/\b(summer|winter|spring|autumn|fall|year-round)\b/)
  const season = seasonMatch ? seasonMatch[1] : null

  // Top segment
  const topSegment = intelligence.guestDistribution?.segments?.[0]?.label?.toLowerCase()

  // Audience — domestic vs international
  const domestic = intelligence.geographic?.domesticShare ?? 0
  const international = intelligence.geographic?.internationalShare ?? 0
  const audience = international > domestic ? "international" : "domestic"

  // Arrival pattern — look in behaviourMetrics for arrival day
  const arrivalMetric = intelligence.behaviourMetrics?.find(
    m => m.label.toLowerCase().includes("arrival") || m.label.toLowerCase().includes("check-in"),
  )
  const arrivalDay = arrivalMetric?.value || null

  // Build sentence
  if (season) parts.push(`A ${season}-led`)
  else parts.push("A")

  if (topSegment) parts.push(`home for ${topSegment}`)
  else parts.push("home")

  const suffixes: string[] = []
  if (arrivalDay) suffixes.push(`${arrivalDay} arrivals`)
  suffixes.push(`a mostly ${audience} audience`)

  return `${parts.join(" ")}, with ${suffixes.join(" and ")}.`
}

// ---------------------------------------------------------------------------
// Decision Context — deterministic explanation for Display Preview
// ---------------------------------------------------------------------------

const STAGE_LABELS: Record<string, string> = {
  before_checkin: "Before check-in",
  arrival_day: "Arrival day",
  during_stay: "During stay",
  checkout_evening: "Checkout evening",
  checkout_morning: "Checkout morning",
  between_bookings: "Between bookings",
  night_trivia: "Night mode",
}

export { STAGE_LABELS }

/**
 * Derive the current season position from SeasonContext month arrays.
 * Returns "Peak season", "Shoulder season", "Off-season", or null.
 */
function deriveSeasonPosition(sc: SeasonContext | null | undefined): string | null {
  if (!sc || !sc.current_month_local) return null
  const month = sc.current_month_local
  if (Array.isArray(sc.peak_months) && sc.peak_months.includes(month)) return "Peak season"
  if (Array.isArray(sc.shoulder_months) && sc.shoulder_months.includes(month)) return "Shoulder season"
  if (Array.isArray(sc.off_season_months) && sc.off_season_months.includes(month)) return "Off-season"
  return null
}

/**
 * Derive "Day N of M" label from booking dates, or null if not in an active stay.
 */
function deriveStayDay(nextBooking: OverviewPayload["nextBooking"]): string | null {
  if (!nextBooking) return null
  const today = new Date().toISOString().slice(0, 10)
  const { arrival_date, departure_date, nights } = nextBooking
  if (!arrival_date || !departure_date || today < arrival_date || today >= departure_date) return null
  const arrivalMs = new Date(arrival_date + "T12:00:00").getTime()
  const todayMs = new Date(today + "T12:00:00").getTime()
  const dayNum = Math.floor((todayMs - arrivalMs) / 86400000) + 1
  return `Day ${dayNum} of ${nights}`
}

export function buildDecisionContext(
  overview: OverviewPayload | null,
  seasonContext: SeasonContext | null | undefined,
): DecisionContext {
  const strategy = overview?.stayStrategy?.strategy ?? null
  const nextBooking = overview?.nextBooking ?? null
  const weather = overview?.weather ?? null
  const hour = new Date().getHours()

  // Resolve stay stage
  let stayStage: StayStage | null = null
  if (nextBooking) {
    const phase = resolveCurrentPhase(nextBooking.arrival_date, nextBooking.departure_date)
    if (phase) {
      stayStage = phase as StayStage
    } else {
      stayStage = "between_bookings"
    }
    // Detect night trivia window (22:00–06:00) during active stay
    if (stayStage && stayStage !== "before_checkin" && stayStage !== "between_bookings") {
      if (hour >= 22 || hour < 6) {
        stayStage = "night_trivia"
      }
    }
  } else {
    stayStage = "between_bookings"
  }

  // Group type and size
  const groupType = strategy?.party_profile as DecisionContext["groupType"] ?? null
  const adults = strategy?.adults ?? nextBooking?.adults ?? null
  const children = strategy?.children ?? nextBooking?.children ?? null
  const groupSize = (adults != null || children != null)
    ? (Math.max(0, Number(adults) || 0) + Math.max(0, Number(children) || 0)) || null
    : null

  // Stay length
  const nights = strategy?.nights ?? nextBooking?.nights ?? null
  const stayProfile = strategy?.stay_profile ?? null
  const isLongStay = stayProfile === "long"

  // Night mode — only "active" when BOTH configured AND in the time window
  const nightModeConfigured = strategy?.night_mode ?? false
  const nightModeActive = nightModeConfigured && (hour >= 22 || hour < 6)

  // Season position — derived from month arrays, not classification label
  const seasonPosition = deriveSeasonPosition(seasonContext)

  // Content source — from strategy metadata already in overview
  const contentSource = strategy?.powered_by_ai
    ? "AI-generated"
    : (strategy?.source === "fallback" ? "Deterministic fallback" : null)
  const contentModel = strategy?.powered_by_ai
    ? (strategy.generated_by_model || null)
    : null

  // Stage label
  const stayStageLabel = stayStage ? (STAGE_LABELS[stayStage] ?? null) : null

  return {
    stayStage,
    stayStageLabel,
    stayDay: deriveStayDay(nextBooking),
    groupType,
    groupSize,
    isLongStay,
    lengthOfStay: nights,
    seasonPosition,
    temperatureC: weather?.temperature_c ?? null,
    weatherLabel: weather?.weather_label ?? null,
    nightModeActive,
    nightModeConfigured,
    contentSource,
    contentModel,
  }
}

export function buildExplanationSummary(ctx: DecisionContext): string {
  // Priority-ordered deterministic rules — stayStage drives phase-specific summaries
  if (ctx.stayStage === null) return "No active stay \u2014 preview using default context."
  if (ctx.stayStage === "between_bookings") return "Between bookings \u2014 operational status displayed."
  if (ctx.stayStage === "night_trivia") return "Night mode active \u2014 simplified display content."
  if (ctx.stayStage === "before_checkin") return "Focusing on pre-arrival preparation and essentials."
  if (ctx.stayStage === "arrival_day") return "Focusing on orientation and check-in essentials."
  if (ctx.stayStage === "checkout_evening") return "Preparing for smooth departure tomorrow."
  if (ctx.stayStage === "checkout_morning") return "Checkout day \u2014 departure guidance prioritised."
  // during_stay — secondary environmental rules
  if (ctx.temperatureC != null && ctx.temperatureC < 8) return "Prioritising indoor experiences and warm dining options."
  if (ctx.isLongStay) return "Introducing variety across multiple days."
  if (ctx.seasonPosition === "Peak season") return "Highlighting high-demand seasonal attractions."
  return "Curating local recommendations for today."
}

/**
 * Build a human-readable sentence for the AI's current focus.
 * Used as the collapsed AI Activity summary.
 */
export function buildAiFocusSentence(
  overview: OverviewPayload,
  seasonContext: SeasonContext | null | undefined,
): string {
  const ctx = buildDecisionContext(overview, seasonContext ?? null)
  const base = buildExplanationSummary(ctx)
  const nb = overview.nextBooking
  if (!nb || ctx.stayStage === "between_bookings" || ctx.stayStage === null) return base
  const guest = nb.guest_name || "upcoming guest"
  const arrival = formatDateLabel(nb.arrival_date)
  return `${base.replace(/\.$/, "")} for ${guest} arriving ${arrival}.`
}

// ---------------------------------------------------------------------------
// Command Centre — Snapshot diff (Since You Were Last Here)
// TODO: Replace localStorage snapshot with server-side diff endpoint
// ---------------------------------------------------------------------------

const SNAPSHOT_PREFIX = "sf_overview_snap_"

export function getLastSnapshot(propertyId: string): OverviewSnapshot | null {
  try {
    const raw = localStorage.getItem(`${SNAPSHOT_PREFIX}${propertyId}`)
    if (!raw) return null
    return JSON.parse(raw) as OverviewSnapshot
  } catch {
    return null
  }
}

export function saveSnapshot(
  propertyId: string,
  overview: OverviewPayload,
  reviewTeaser: ReviewTeaserPayload | undefined,
  performanceSummary?: PerformanceSummaryResponse | null,
): void {
  try {
    const health = overview.health
    const healthKeys = health ? (Object.keys(health) as Array<keyof typeof health>) : []
    const healthIssues = healthKeys.filter(k => health![k] !== "healthy" && health![k] !== "neutral").length

    const snapshot: OverviewSnapshot = {
      reputation_score: reviewTeaser?.reputation_score ?? null,
      total_reviews_count: reviewTeaser?.total_reviews_count ?? 0,
      health_issues: healthIssues,
      milestones_earned: overview.milestones?.total_earned ?? 0,
      revenue_value: performanceSummary?.kpis?.revenue?.value ?? null,
      revenue_currency: performanceSummary?.kpis?.revenue?.currency ?? null,
      bookings_value: performanceSummary?.kpis?.bookings?.value ?? 0,
      timestamp: new Date().toISOString(),
    }
    localStorage.setItem(`${SNAPSHOT_PREFIX}${propertyId}`, JSON.stringify(snapshot))
  } catch {
    // localStorage not available — silently degrade
  }
}

export function buildSinceLastVisitItems(
  propertyId: string,
  overview: OverviewPayload,
  reviewTeaser: ReviewTeaserPayload | undefined,
  performanceSummary?: PerformanceSummaryResponse | null,
): SinceLastVisitItem[] {
  const prev = getLastSnapshot(propertyId)
  if (!prev) return [] // First visit — no diff

  const items: SinceLastVisitItem[] = []

  // 1. Reputation score change
  if (reviewTeaser?.reputation_score != null && prev.reputation_score != null) {
    const delta = reviewTeaser.reputation_score - prev.reputation_score
    if (delta !== 0) {
      const sign = delta > 0 ? "+" : ""
      items.push({
        kind: "reputation_change",
        label: `Reputation score ${sign}${delta}`,
        detail: `Now ${reviewTeaser.reputation_score}`,
      })
    }
  }

  // 2. New reviews
  const currentReviewCount = reviewTeaser?.total_reviews_count ?? 0
  const newReviews = currentReviewCount - prev.total_reviews_count
  if (newReviews > 0) {
    items.push({
      kind: "new_reviews",
      label: `${newReviews} new review${newReviews !== 1 ? "s" : ""} since last visit`,
    })
  }

  // 3. Commercial change
  const currentRevenue = performanceSummary?.kpis?.revenue?.value ?? null
  const currentCurrency = performanceSummary?.kpis?.revenue?.currency ?? prev.revenue_currency ?? "USD"
  if (currentRevenue != null && prev.revenue_value != null) {
    const delta = currentRevenue - prev.revenue_value
    if (Math.abs(delta) >= 1) {
      const money = createSafeCurrencyFormatter(currentCurrency)
      const sign = delta > 0 ? "+" : ""
      items.push({
        kind: "commercial_change",
        label: `Booked revenue ${sign}${money.format(delta)}`,
        detail: `Now ${money.format(currentRevenue)} this month`,
      })
    }
  } else {
    const currentBookings = performanceSummary?.kpis?.bookings?.value ?? 0
    const bookingDelta = currentBookings - (prev.bookings_value ?? 0)
    if (bookingDelta > 0) {
      items.push({
        kind: "commercial_change",
        label: `${bookingDelta} more booking${bookingDelta !== 1 ? "s" : ""} on the books`,
        detail: `Now ${currentBookings}`,
      })
    }
  }

  // 4. System issues (only if count increased)
  const health = overview.health
  const healthKeys = health ? (Object.keys(health) as Array<keyof typeof health>) : []
  const currentIssues = healthKeys.filter(k => health![k] !== "healthy" && health![k] !== "neutral").length
  if (currentIssues > prev.health_issues && currentIssues > 0) {
    items.push({
      kind: "system_issue",
      label: `${currentIssues} system issue${currentIssues !== 1 ? "s" : ""} need attention`,
    })
  }

  // 5. New milestones
  const currentMilestones = overview.milestones?.total_earned ?? 0
  const newMilestones = currentMilestones - prev.milestones_earned
  if (newMilestones > 0) {
    items.push({
      kind: "milestone",
      label: `${newMilestones} new milestone${newMilestones !== 1 ? "s" : ""} earned`,
    })
  }

  return items
}

// ---------------------------------------------------------------------------
// Command Centre — Decision Queue helpers
// ---------------------------------------------------------------------------

const DISMISS_PREFIX = "sf_dq_dismiss_"
const REMIND_PREFIX = "sf_dq_remind_"

/** djb2 content hash → hex string. Stable across recommendation reorder. */
export function stableActionKey(action: string, priority: string, metricTarget: string | null): string {
  const input = `${action}|${priority}|${metricTarget || ""}`
  let hash = 5381
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0
  }
  return (hash >>> 0).toString(16)
}

export function isDismissed(key: string): boolean {
  try {
    return sessionStorage.getItem(`${DISMISS_PREFIX}${key}`) === "1"
  } catch {
    return false
  }
}

export function dismissAction(key: string): void {
  try {
    sessionStorage.setItem(`${DISMISS_PREFIX}${key}`, "1")
  } catch { /* sessionStorage unavailable */ }
}

export function isRemindLater(key: string): boolean {
  try {
    return sessionStorage.getItem(`${REMIND_PREFIX}${key}`) === "1"
  } catch {
    return false
  }
}

export function remindLater(key: string): void {
  try {
    sessionStorage.setItem(`${REMIND_PREFIX}${key}`, "1")
  } catch { /* sessionStorage unavailable */ }
}

/** Route heuristic: derive target page from recommendation action text. */
export function resolveActionSurface(actionText: unknown): string {
  const text = typeof actionText === "string" ? actionText : ""
  if (/review|reputation|rating/i.test(text)) return "reviews"
  if (/setting|config|alert/i.test(text)) return "settings"
  return "performance"
}

// ---------------------------------------------------------------------------
// Command Centre — Analytics stubs
// TODO: Wire to real analytics provider (Plausible / PostHog / custom)
// ---------------------------------------------------------------------------

export function trackOverviewEvent(event: string, properties?: Record<string, unknown>): void {
  if (typeof window !== "undefined" && import.meta.env.DEV) {
    console.debug(`[overview:analytics] ${event}`, properties)
  }
}

// ---------------------------------------------------------------------------
// Command Centre — Decision title extraction
// ---------------------------------------------------------------------------

/**
 * Extract a short scannable title from an AI recommendation action string.
 * Actions use a consistent "Topic: detail" format. Returns { title, detail }.
 */
export function extractDecisionTitle(action: unknown): { title: string; detail: string } {
  const text = typeof action === "string" ? action.trim() : ""
  if (!text) {
    return {
      title: "Recommendation",
      detail: "Stayframe surfaced an action, but the recommendation detail was incomplete.",
    }
  }
  const colonIdx = text.indexOf(":")
  if (colonIdx > 0 && colonIdx < 80) {
    return {
      title: text.slice(0, colonIdx).trim(),
      detail: text.slice(colonIdx + 1).trim(),
    }
  }
  const sentenceEnd = text.search(/[.!?]/)
  if (sentenceEnd > 0 && sentenceEnd <= 60) {
    return { title: text.slice(0, sentenceEnd).trim(), detail: text }
  }
  return {
    title: text.length > 60 ? text.slice(0, 57) + "\u2026" : text,
    detail: text,
  }
}

// ---------------------------------------------------------------------------
// Command Centre — Freshness computation
// ---------------------------------------------------------------------------

/**
 * Compute a freshness label and tier from an ISO timestamp.
 * Tier: "live" (<30min), "recent" (<24h), "stale" (older).
 */
export function computeFreshnessLabel(isoString: string | null): { label: string; tier: "live" | "recent" | "stale" } {
  if (!isoString) return { label: "No data", tier: "stale" }
  const date = new Date(isoString)
  if (Number.isNaN(date.getTime())) return { label: "Unknown", tier: "stale" }
  const diffMs = Date.now() - date.getTime()
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 30) return { label: "Live", tier: "live" }
  if (diffHr < 1) return { label: `${diffMin}m ago`, tier: "recent" }
  if (diffHr < 24) return { label: `${diffHr}h ago`, tier: "recent" }
  if (diffDay < 7) return { label: `${diffDay}d ago`, tier: "stale" }
  if (diffDay < 30) return { label: `${Math.floor(diffDay / 7)}w ago`, tier: "stale" }
  return { label: date.toLocaleDateString(undefined, { month: "short", day: "numeric" }), tier: "stale" }
}
